import { useEffect, useMemo, useState } from "react";
import {
	Box,
	Button,
	Card,
	CardContent,
	CardMedia,
	Container,
	Divider,
	List,
	ListItem,
	ListItemButton,
	TextField,
	Typography
} from "@mui/material";
import {
	addDoc,
	collection,
	deleteDoc,
	doc,
	getDocs,
	orderBy,
	query,
	serverTimestamp,
	updateDoc,
	writeBatch
} from "firebase/firestore";
import { db } from "../firebase";
import NavBarAdm from "./navbarAdm";

const AdmHandysSchule = () => {
	const [collectionsList, setCollectionsList] = useState([]);
	const [selectedCollection, setSelectedCollection] = useState(null);
	const [items, setItems] = useState([]);

	const [newCollectionName, setNewCollectionName] = useState("");
	const [newCollectionPosition, setNewCollectionPosition] = useState(1);
	const [collectionOrderDrafts, setCollectionOrderDrafts] = useState({});
	const [newItemTitle, setNewItemTitle] = useState("");
	const [newItemPhotoUrl, setNewItemPhotoUrl] = useState("");
	const [newItemPosition, setNewItemPosition] = useState(1);

	const [editingItemId, setEditingItemId] = useState(null);
	const [editingTitle, setEditingTitle] = useState("");
	const [editingPhotoUrl, setEditingPhotoUrl] = useState("");

	const [statusMessage, setStatusMessage] = useState("");
	const [isLoadingCollections, setIsLoadingCollections] = useState(false);
	const [isLoadingItems, setIsLoadingItems] = useState(false);
	const [isImageUploading, setIsImageUploading] = useState(false);

	const compressImageToBase64 = (file) =>
		new Promise((resolve, reject) => {
			if (!file.type.startsWith("image/")) {
				reject(new Error("Bitte eine Bilddatei auswaehlen."));
				return;
			}

			if (file.size > 3 * 1024 * 1024) {
				reject(new Error("Bild ist zu gross. Maximal 3MB."));
				return;
			}

			const canvas = document.createElement("canvas");
			const ctx = canvas.getContext("2d");
			if (!ctx) {
				reject(new Error("Canvas konnte nicht initialisiert werden."));
				return;
			}
			const img = new Image();
			img.onload = () => {
				let { width, height } = img;
				let maxWidth;
				let maxHeight;
				let quality;

				if (file.size > 2 * 1024 * 1024) {
					maxWidth = 1200;
					maxHeight = 900;
					quality = 0.8;
				} else if (file.size > 1 * 1024 * 1024) {
					maxWidth = 1400;
					maxHeight = 1000;
					quality = 0.85;
				} else {
					maxWidth = 1600;
					maxHeight = 1200;
					quality = 0.9;
				}

				if (width > maxWidth || height > maxHeight) {
					const ratio = Math.min(maxWidth / width, maxHeight / height);
					width = Math.round(width * ratio);
					height = Math.round(height * ratio);
				}

				canvas.width = width;
				canvas.height = height;
				ctx.drawImage(img, 0, 0, width, height);

				let base64String = canvas.toDataURL("image/jpeg", quality);
				let base64Size = Math.round((base64String.length * 3) / 4);
				let attempts = 0;

				while (base64Size > 700 * 1024 && attempts < 3) {
					attempts += 1;
					quality = Math.max(quality - 0.1, 0.4);

					if (attempts > 1) {
						width = Math.round(width * 0.9);
						height = Math.round(height * 0.9);
						canvas.width = width;
						canvas.height = height;
						ctx.drawImage(img, 0, 0, width, height);
					}

					base64String = canvas.toDataURL("image/jpeg", quality);
					base64Size = Math.round((base64String.length * 3) / 4);
				}

				if (base64Size > 700 * 1024) {
					reject(new Error("Bild konnte nicht ausreichend komprimiert werden."));
					return;
				}

				resolve(base64String);
			};
			img.onerror = () => reject(new Error("Fehler beim Laden des Bildes."));
			img.src = URL.createObjectURL(file);
		});

	const collectionsRef = useMemo(
		() => collection(db, "handys_schule_collections"),
		[]
	);

	const clampPosition = (value, maxValue) => {
		const numericValue = Number(value);
		if (!Number.isFinite(numericValue)) return maxValue;
		return Math.min(Math.max(Math.floor(numericValue), 1), maxValue);
	};

	const normalizeOrder = async (itemsRef, list) => {
		const needsUpdate = list.some((item, index) => item.order !== index + 1);
		if (!needsUpdate) return list;
		const batch = writeBatch(db);
		list.forEach((item, index) => {
			const itemRef = doc(itemsRef, item.id);
			batch.update(itemRef, { order: index + 1 });
		});
		await batch.commit();
		return list.map((item, index) => ({ ...item, order: index + 1 }));
	};

	const normalizeCollectionOrder = async (list) => {
		const needsUpdate = list.some((item, index) => item.order !== index + 1);
		if (!needsUpdate) return list;
		const batch = writeBatch(db);
		list.forEach((item, index) => {
			const itemRef = doc(db, "handys_schule_collections", item.id);
			batch.update(itemRef, { order: index + 1 });
		});
		await batch.commit();
		return list.map((item, index) => ({ ...item, order: index + 1 }));
	};

	const loadCollections = async () => {
		setIsLoadingCollections(true);
		try {
			const q = query(collectionsRef, orderBy("order", "asc"));
			const snapshot = await getDocs(q);
			let list = snapshot.docs.map((docSnap) => ({
				id: docSnap.id,
				...docSnap.data()
			}));
			list = await normalizeCollectionOrder(list);
			setCollectionsList(list);
			setNewCollectionPosition((prev) => clampPosition(prev, list.length + 1));
			setCollectionOrderDrafts(
				list.reduce((acc, item, index) => {
					acc[item.id] = item.order || index + 1;
					return acc;
				}, {})
			);
		} catch (error) {
			console.error("Failed to load collections:", error);
			setStatusMessage("Fehler beim Laden der Sammlungen.");
		} finally {
			setIsLoadingCollections(false);
		}
	};

	const loadItems = async (collectionId) => {
		if (!collectionId) return;
		setIsLoadingItems(true);
		try {
			const itemsRef = collection(db, "handys_schule_collections", collectionId, "items");
			const q = query(itemsRef, orderBy("order", "asc"));
			const snapshot = await getDocs(q);
			let list = snapshot.docs.map((docSnap) => ({
				id: docSnap.id,
				...docSnap.data()
			}));
			list = await normalizeOrder(itemsRef, list);
			setItems(list);
			setNewItemPosition((prev) => clampPosition(prev, list.length + 1));
		} catch (error) {
			console.error("Failed to load items:", error);
			setStatusMessage("Fehler beim Laden der Eintraege.");
		} finally {
			setIsLoadingItems(false);
		}
	};

	useEffect(() => {
		void loadCollections();
	}, []);

	const handleAddCollection = async () => {
		const trimmed = newCollectionName.trim();
		if (!trimmed) return;
		try {
			const nextPosition = clampPosition(newCollectionPosition, collectionsList.length + 1);
			const batch = writeBatch(db);
			collectionsList
				.filter((col) => (col.order || 0) >= nextPosition)
				.forEach((col) => {
					const colRef = doc(db, "handys_schule_collections", col.id);
					batch.update(colRef, { order: (col.order || 0) + 1 });
				});
			const newColRef = doc(collectionsRef);
			batch.set(newColRef, {
				name: trimmed,
				order: nextPosition,
				createdAt: serverTimestamp()
			});
			await batch.commit();
			setNewCollectionName("");
			setNewCollectionPosition(collectionsList.length + 2);
			setStatusMessage("Sammlung erstellt.");
			await loadCollections();
		} catch (error) {
			console.error("Failed to add collection:", error);
			setStatusMessage("Fehler beim Erstellen der Sammlung.");
		}
	};

	const handleSelectCollection = async (col) => {
		setSelectedCollection(col);
		setItems([]);
		setEditingItemId(null);
		setEditingTitle("");
		setEditingPhotoUrl("");
		setNewItemPosition(1);
		await loadItems(col.id);
	};

	const handleCollectionOrderChange = (collectionId, value) => {
		setCollectionOrderDrafts((prev) => ({ ...prev, [collectionId]: value }));
	};

	const handleApplyCollectionOrder = async (collectionId) => {
		const desired = clampPosition(
			collectionOrderDrafts[collectionId],
			collectionsList.length
		);
		const ordered = [...collectionsList].sort((a, b) => (a.order || 0) - (b.order || 0));
		const currentIndex = ordered.findIndex((col) => col.id === collectionId);
		if (currentIndex === -1) return;
		const [moved] = ordered.splice(currentIndex, 1);
		ordered.splice(desired - 1, 0, moved);
		const batch = writeBatch(db);
		ordered.forEach((col, index) => {
			const colRef = doc(db, "handys_schule_collections", col.id);
			batch.update(colRef, { order: index + 1 });
		});
		await batch.commit();
		setCollectionsList(ordered.map((col, index) => ({ ...col, order: index + 1 })));
		setCollectionOrderDrafts(
			ordered.reduce((acc, col, index) => {
				acc[col.id] = index + 1;
				return acc;
			}, {})
		);
	};

	const handleAddItem = async () => {
		if (!selectedCollection) return;
		const title = newItemTitle.trim();
		const photoUrl = newItemPhotoUrl.trim();
		if (!title || !photoUrl) return;
		try {
			const itemsRef = collection(
				db,
				"handys_schule_collections",
				selectedCollection.id,
				"items"
			);
			const nextPosition = clampPosition(newItemPosition, items.length + 1);
			const batch = writeBatch(db);
			items
				.filter((item) => (item.order || 0) >= nextPosition)
				.forEach((item) => {
					const itemRef = doc(itemsRef, item.id);
					batch.update(itemRef, { order: (item.order || 0) + 1 });
				});
			const newItemRef = doc(itemsRef);
			batch.set(newItemRef, {
				title,
				photoUrl,
				order: nextPosition,
				createdAt: serverTimestamp()
			});
			await batch.commit();
			setNewItemTitle("");
			setNewItemPhotoUrl("");
			setNewItemPosition(items.length + 2);
			setStatusMessage("Eintrag erstellt.");
			await loadItems(selectedCollection.id);
		} catch (error) {
			console.error("Failed to add item:", error);
			setStatusMessage("Fehler beim Erstellen des Eintrags.");
		}
	};

	const handleNewPhotoUpload = async (event) => {
		const file = event.target.files?.[0];
		if (!file) return;
		setIsImageUploading(true);
		try {
			const base64String = await compressImageToBase64(file);
			setNewItemPhotoUrl(base64String);
			setStatusMessage("Foto geladen und komprimiert.");
		} catch (error) {
			setStatusMessage(error.message);
		} finally {
			setIsImageUploading(false);
			event.target.value = "";
		}
	};

	const handleStartEdit = (item) => {
		setEditingItemId(item.id);
		setEditingTitle(item.title || "");
		setEditingPhotoUrl(item.photoUrl || "");
	};

	const handleCancelEdit = () => {
		setEditingItemId(null);
		setEditingTitle("");
		setEditingPhotoUrl("");
	};

	const handleSaveEdit = async () => {
		if (!selectedCollection || !editingItemId) return;
		const title = editingTitle.trim();
		const photoUrl = editingPhotoUrl.trim();
		if (!title || !photoUrl) return;
		try {
			const itemRef = doc(
				db,
				"handys_schule_collections",
				selectedCollection.id,
				"items",
				editingItemId
			);
			await updateDoc(itemRef, {
				title,
				photoUrl,
				updatedAt: serverTimestamp()
			});
			setStatusMessage("Eintrag aktualisiert.");
			setEditingItemId(null);
			setEditingTitle("");
			setEditingPhotoUrl("");
			await loadItems(selectedCollection.id);
		} catch (error) {
			console.error("Failed to update item:", error);
			setStatusMessage("Fehler beim Aktualisieren des Eintrags.");
		}
	};

	const handleEditPhotoUpload = async (event) => {
		const file = event.target.files?.[0];
		if (!file) return;
		setIsImageUploading(true);
		try {
			const base64String = await compressImageToBase64(file);
			setEditingPhotoUrl(base64String);
			setStatusMessage("Foto geladen und komprimiert.");
		} catch (error) {
			setStatusMessage(error.message);
		} finally {
			setIsImageUploading(false);
			event.target.value = "";
		}
	};

	const handleDeleteItem = async (itemId) => {
		if (!selectedCollection || !itemId) return;
		const confirmed = window.confirm("Eintrag wirklich loeschen?");
		if (!confirmed) return;
		try {
			const itemRef = doc(
				db,
				"handys_schule_collections",
				selectedCollection.id,
				"items",
				itemId
			);
			await deleteDoc(itemRef);
			setStatusMessage("Eintrag geloescht.");
			await loadItems(selectedCollection.id);
		} catch (error) {
			console.error("Failed to delete item:", error);
			setStatusMessage("Fehler beim Loeschen des Eintrags.");
		}
	};

	const handleDeleteCollection = async () => {
		if (!selectedCollection) return;
		const confirmed = window.confirm("Sammlung wirklich loeschen? Alle Eintraege werden entfernt.");
		if (!confirmed) return;
		try {
			const itemsRef = collection(
				db,
				"handys_schule_collections",
				selectedCollection.id,
				"items"
			);
			const snapshot = await getDocs(itemsRef);
			let batch = writeBatch(db);
			let operationCount = 0;
			snapshot.docs.forEach((docSnap) => {
				batch.delete(docSnap.ref);
				operationCount += 1;
				if (operationCount === 450) {
					batch.commit();
					batch = writeBatch(db);
					operationCount = 0;
				}
			});
			if (operationCount > 0) {
				await batch.commit();
			}

			const collectionRef = doc(db, "handys_schule_collections", selectedCollection.id);
			await deleteDoc(collectionRef);

			setSelectedCollection(null);
			setItems([]);
			setStatusMessage("Sammlung geloescht.");
			await loadCollections();
		} catch (error) {
			console.error("Failed to delete collection:", error);
			setStatusMessage("Fehler beim Loeschen der Sammlung.");
		}
	};

	return (
		<>
			<NavBarAdm />
			<Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
				<Typography variant="h4" gutterBottom>
					Adm Handys Schule
				</Typography>

				{statusMessage && (
					<Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
						{statusMessage}
					</Typography>
				)}

				<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "300px 1fr" }, gap: 3 }}>
					<Box>
						<Card sx={{ mb: 2 }}>
							<CardContent>
								<Typography variant="h6" gutterBottom>
									Neue Sammlung
								</Typography>
								<TextField
									label="Name"
									value={newCollectionName}
									onChange={(event) => setNewCollectionName(event.target.value)}
									fullWidth
									size="small"
									sx={{ mb: 1.5 }}
								/>
								<TextField
									label="Position"
									type="number"
									value={newCollectionPosition}
									onChange={(event) => setNewCollectionPosition(event.target.value)}
									fullWidth
									size="small"
									sx={{ mb: 1.5 }}
									inputProps={{ min: 1, max: collectionsList.length + 1 }}
								/>
								<Button variant="contained" onClick={handleAddCollection} fullWidth>
									Sammlung erstellen
								</Button>
							</CardContent>
						</Card>

						<Card>
							<CardContent>
								<Typography variant="h6" gutterBottom>
									Sammlungen
								</Typography>
								{isLoadingCollections && (
									<Typography variant="body2" color="text.secondary">
										Lade Sammlungen...
									</Typography>
								)}
								<List dense disablePadding>
									{collectionsList.map((col, index) => (
										<ListItem key={col.id} disablePadding sx={{ flexDirection: "column", alignItems: "stretch", gap: 1, py: 1 }}>
											<ListItemButton
												selected={selectedCollection?.id === col.id}
												onClick={() => handleSelectCollection(col)}
											>
												<Typography variant="body2" sx={{ fontWeight: 600, mr: 1 }}>
													{index + 1}.
												</Typography>
												<Typography variant="body2">{col.name}</Typography>
											</ListItemButton>
											<Box sx={{ display: "flex", gap: 1, px: 1 }}>
												<TextField
													label="Position"
													type="number"
													value={collectionOrderDrafts[col.id] ?? col.order ?? index + 1}
													onChange={(event) => handleCollectionOrderChange(col.id, event.target.value)}
													size="small"
													sx={{ flex: 1 }}
													inputProps={{ min: 1, max: collectionsList.length }}
												/>
												<Button
													variant="outlined"
													onClick={() => handleApplyCollectionOrder(col.id)}
												>
													Setzen
												</Button>
											</Box>
										</ListItem>
									))}
									{!isLoadingCollections && collectionsList.length === 0 && (
										<Typography variant="body2" color="text.secondary">
											Keine Sammlungen vorhanden.
										</Typography>
									)}
								</List>
								<Button
									color="error"
									variant="outlined"
									onClick={handleDeleteCollection}
									disabled={!selectedCollection}
									sx={{ mt: 1 }}
								>
									Sammlung loeschen
								</Button>
							</CardContent>
						</Card>
					</Box>

					<Box sx={{ px: 0.25 }}>
						<Card sx={{ px: 0.25 }}>
							<CardContent sx={{ px: 0.25, py: 1.5 }}>
								<Typography variant="h6" gutterBottom>
									Eintraege
								</Typography>
								{isLoadingItems && (
									<Typography variant="body2" color="text.secondary">
										Lade Eintraege...
									</Typography>
								)}
								{!isLoadingItems && items.length === 0 && selectedCollection && (
									<Typography variant="body2" color="text.secondary">
										Keine Eintraege vorhanden.
									</Typography>
								)}
								{!selectedCollection && (
									<Typography variant="body2" color="text.secondary">
										Waehlen Sie eine Sammlung aus, um Eintraege zu sehen.
									</Typography>
								)}
								<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, mt: 2 }}>
										{selectedCollection && (
											<Card variant="outlined" sx={{ px: 0.25 }}>
												<CardContent sx={{ px: 0 }}>
													<Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
														Neuer Eintrag
													</Typography>
																		<TextField
																			label="Titel"
																			value={newItemTitle}
																			onChange={(event) => setNewItemTitle(event.target.value)}
																			fullWidth
																			size="small"
																			sx={{ mb: 1.5 }}
																		/>
																		<TextField
																			label="Position"
																			type="number"
																			value={newItemPosition}
																			onChange={(event) => setNewItemPosition(event.target.value)}
																			fullWidth
																			size="small"
																			sx={{ mb: 1.5 }}
																			inputProps={{ min: 1, max: items.length + 1 }}
																		/>
													<Button
														variant="outlined"
														component="label"
														disabled={isImageUploading}
														sx={{ mb: 1.5 }}
													>
														Foto auswaehlen
														<input
															type="file"
															accept="image/*"
															hidden
															onChange={handleNewPhotoUpload}
														/>
													</Button>
													{newItemPhotoUrl && (
														<Box sx={{ mb: 1.5, mx: 0.25 }}>
															<CardMedia
																component="img"
																image={newItemPhotoUrl}
																alt="Neues Foto"
															/>
														</Box>
													)}
																		<Button
																			variant="contained"
																			onClick={handleAddItem}
																			disabled={!newItemTitle.trim() || !newItemPhotoUrl.trim()}
																		>
																			Eintrag erstellen
																		</Button>
												</CardContent>
											</Card>
										)}
										{items.map((item, index) => (
											<Card key={item.id} variant="outlined" sx={{ px: 0.25 }}>
												<CardContent sx={{ px: 0 }}>
													<Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0 }}>
														{`${index + 1}. ${item.title || ""}`.trim()}
													</Typography>
													{item.photoUrl && (
														<CardMedia
															component="img"
															image={item.photoUrl}
															alt={item.title || "Foto"}
														/>
													)}
												{editingItemId === item.id ? (
													<>
														<TextField
															label="Titel"
															value={editingTitle}
															onChange={(event) => setEditingTitle(event.target.value)}
															fullWidth
															size="small"
															sx={{ mb: 1.5 }}
														/>
														<Button
															variant="outlined"
															component="label"
															disabled={isImageUploading}
															sx={{ mb: 1.5 }}
														>
															Foto ersetzen
															<input
																type="file"
																accept="image/*"
																hidden
																onChange={handleEditPhotoUpload}
															/>
														</Button>
														{editingPhotoUrl && (
															<Box sx={{ mb: 1.5, mx: 0.25 }}>
																<CardMedia
																	component="img"
																	image={editingPhotoUrl}
																	alt="Bearbeitetes Foto"
																/>
															</Box>
														)}
														<Box sx={{ display: "flex", gap: 1 }}>
															<Button variant="contained" onClick={handleSaveEdit}>
																Speichern
															</Button>
															<Button variant="outlined" onClick={handleCancelEdit}>
																Abbrechen
															</Button>
															<Button color="error" variant="outlined" onClick={() => handleDeleteItem(item.id)}>
																Loeschen
															</Button>
														</Box>
													</>
												) : (
													<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
														<Button size="small" variant="outlined" onClick={() => handleStartEdit(item)}>
															Bearbeiten
														</Button>
														<Button size="small" color="error" variant="outlined" onClick={() => handleDeleteItem(item.id)}>
															Loeschen
														</Button>
													</Box>
												)}
											</CardContent>
										</Card>
									))}
								</Box>
							</CardContent>
						</Card>
					</Box>
				</Box>
				<Divider sx={{ mt: 4 }} />
			</Container>
		</>
	);
};

export default AdmHandysSchule;
